/**
 * Dashboard sidebar + quick-action navigation config.
 * Credit costs are resolved at runtime from /documents/types + /service-credits.
 */

import { RESUME_DISPLAY_NAME } from './docTypeDisplayNames';

export const PROPOSAL_TEMPLATE_ROUTES = {
  business_plan: '/documents/business-proposal/business-plan/templates',
  grant_proposal: '/documents/business-proposal/grant-proposal/templates',
  ngo_grant_proposal: '/documents/business-proposal/ngo-grant-proposal/templates',
  service_proposal: '/documents/business-proposal/service-proposal/templates',
};

/** @typedef {{ id: string, label: string, route: string, icon: string, iconColor: string, creditKey?: string, kind: 'document'|'utility' }} NavItem */

/** @type {NavItem[]} */
export const DOCUMENT_NAV_ITEMS = [
  {
    id: 'business_plan',
    label: 'Business Plan',
    route: PROPOSAL_TEMPLATE_ROUTES.business_plan,
    icon: 'ti-report-analytics',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    creditKey: 'business_plan_funding',
    kind: 'document',
  },
  {
    id: 'grant_proposal',
    label: 'Business Grant Proposal',
    route: PROPOSAL_TEMPLATE_ROUTES.grant_proposal,
    icon: 'ti-award',
    iconColor: 'text-violet-600 dark:text-violet-400',
    creditKey: 'business_grant_proposal',
    kind: 'document',
  },
  {
    id: 'ngo_grant_proposal',
    label: 'NGO Grant Proposal',
    route: PROPOSAL_TEMPLATE_ROUTES.ngo_grant_proposal,
    icon: 'ti-heart-handshake',
    iconColor: 'text-amber-600 dark:text-amber-400',
    creditKey: 'ngo_grant_proposal',
    kind: 'document',
  },
  {
    id: 'service_proposal',
    label: 'Service Proposal',
    route: PROPOSAL_TEMPLATE_ROUTES.service_proposal,
    icon: 'ti-briefcase',
    iconColor: 'text-sky-600 dark:text-sky-400',
    creditKey: 'service_proposal',
    kind: 'document',
  },
  {
    id: 'purchase_order',
    label: 'Purchase Order',
    route: '/documents/purchase-order/templates',
    icon: 'ti-shopping-cart',
    iconColor: 'text-orange-600 dark:text-orange-400',
    creditKey: 'purchase_order',
    kind: 'document',
  },
  {
    id: 'resume',
    label: RESUME_DISPLAY_NAME,
    route: '/documents/resume/templates',
    icon: 'ti-file-description',
    iconColor: 'text-blue-600 dark:text-blue-400',
    creditKey: 'resume',
    kind: 'document',
  },
  {
    id: 'invoice',
    label: 'Invoice',
    route: '/documents/invoice/templates',
    icon: 'ti-receipt',
    iconColor: 'text-rose-600 dark:text-rose-400',
    creditKey: 'invoice',
    kind: 'document',
  },
  {
    id: 'proforma_invoice',
    label: 'Proforma Invoice',
    route: '/documents/proforma-invoice/templates',
    icon: 'ti-file-invoice',
    iconColor: 'text-amber-600 dark:text-amber-400',
    creditKey: 'proforma_invoice',
    kind: 'document',
  },
  {
    id: 'quotation',
    label: 'Quotation',
    route: '/documents/quotation/templates',
    icon: 'ti-calculator',
    iconColor: 'text-indigo-600 dark:text-indigo-400',
    creditKey: 'quotation',
    kind: 'document',
  },
  {
    id: 'waybill',
    label: 'Waybill',
    route: '/documents/waybill/templates',
    icon: 'ti-truck',
    iconColor: 'text-teal-600 dark:text-teal-400',
    creditKey: 'waybill',
    kind: 'document',
  },
  {
    id: 'delivery_note',
    label: 'Delivery Note',
    route: '/documents/delivery-note/templates',
    icon: 'ti-package',
    iconColor: 'text-cyan-600 dark:text-cyan-400',
    creditKey: 'delivery_note',
    kind: 'document',
  },
];

/** @type {NavItem[]} */
export const TOOL_NAV_ITEMS = [
  {
    id: 'pdf_compression',
    label: 'Compress PDF',
    route: '/services/pdf-compression',
    icon: 'ti-file-zip',
    iconColor: 'text-red-600 dark:text-red-400',
    creditKey: 'pdf_compression',
    kind: 'utility',
  },
  {
    id: 'visa_photo',
    label: 'Passport Photo',
    route: '/services/visa-passport-photo',
    icon: 'ti-camera',
    iconColor: 'text-fuchsia-600 dark:text-fuchsia-400',
    creditKey: 'visa_photo',
    kind: 'utility',
  },
];

/** Quick Actions — Tier 1 (flagship) then Tier 2 (everyday). Order fixed within each tier. */
export const QUICK_ACTION_TIER1 = [
  {
    id: 'business_plan',
    label: 'Business Plan',
    route: PROPOSAL_TEMPLATE_ROUTES.business_plan,
    creditKey: 'business_plan_funding',
    icon: 'ti-report-analytics',
    description: 'Build a bank-ready plan with financials, market analysis, and executive summary.',
    cardBg: 'bg-emerald-100/90 dark:bg-emerald-950/55',
    cardBorder: 'border-emerald-500/50 dark:border-emerald-500/40',
    iconWrap: 'bg-white/90 dark:bg-emerald-900/40 ring-emerald-200/80 dark:ring-emerald-700',
    iconColor: 'text-emerald-700 dark:text-emerald-300',
    titleColor: 'text-emerald-950 dark:text-emerald-50',
    descColor: 'text-emerald-900/75 dark:text-emerald-100/80',
    creditBadge: 'bg-emerald-600 text-white dark:bg-emerald-500',
  },
  {
    id: 'grant_proposal',
    label: 'Business Grant Proposal',
    route: PROPOSAL_TEMPLATE_ROUTES.grant_proposal,
    creditKey: 'business_grant_proposal',
    icon: 'ti-award',
    description: 'Generate a fundable business grant proposal with SWOT, risk table, and smart narratives.',
    cardBg: 'bg-violet-100/90 dark:bg-violet-950/55',
    cardBorder: 'border-violet-500/50 dark:border-violet-500/40',
    iconWrap: 'bg-white/90 dark:bg-violet-900/40 ring-violet-200/80 dark:ring-violet-700',
    iconColor: 'text-violet-700 dark:text-violet-300',
    titleColor: 'text-violet-950 dark:text-violet-50',
    descColor: 'text-violet-900/75 dark:text-violet-100/80',
    creditBadge: 'bg-violet-600 text-white dark:bg-violet-500',
  },
  {
    id: 'ngo_grant_proposal',
    label: 'NGO Grant Proposal',
    route: PROPOSAL_TEMPLATE_ROUTES.ngo_grant_proposal,
    creditKey: 'ngo_grant_proposal',
    icon: 'ti-heart-handshake',
    description: 'Build a nonprofit grant proposal with objectives, budget narrative, and M&E plan.',
    cardBg: 'bg-amber-100/90 dark:bg-amber-950/55',
    cardBorder: 'border-amber-500/50 dark:border-amber-500/40',
    iconWrap: 'bg-white/90 dark:bg-amber-900/40 ring-amber-200/80 dark:ring-amber-700',
    iconColor: 'text-amber-700 dark:text-amber-300',
    titleColor: 'text-amber-950 dark:text-amber-50',
    descColor: 'text-amber-900/75 dark:text-amber-100/80',
    creditBadge: 'bg-amber-600 text-white dark:bg-amber-500',
  },
  {
    id: 'service_proposal',
    label: 'Service Proposal',
    route: PROPOSAL_TEMPLATE_ROUTES.service_proposal,
    creditKey: 'service_proposal',
    icon: 'ti-briefcase',
    description: 'Win clients with scope, pricing, deliverables, and polished service proposals.',
    cardBg: 'bg-sky-100/90 dark:bg-sky-950/55',
    cardBorder: 'border-sky-500/50 dark:border-sky-500/40',
    iconWrap: 'bg-white/90 dark:bg-sky-900/40 ring-sky-200/80 dark:ring-sky-700',
    iconColor: 'text-sky-700 dark:text-sky-300',
    titleColor: 'text-sky-950 dark:text-sky-50',
    descColor: 'text-sky-900/75 dark:text-sky-100/80',
    creditBadge: 'bg-sky-600 text-white dark:bg-sky-500',
  },
];

/** Tier 1 row 1 — three flagship proposals across the top row. */
export const QUICK_ACTION_TIER1_ROW1 = QUICK_ACTION_TIER1.slice(0, 3);

/** Tier 1 row 2 — Service Proposal centered under Business Grant Proposal. */
export const QUICK_ACTION_TIER1_ROW2_CENTER = QUICK_ACTION_TIER1.find(
  (item) => item.id === 'service_proposal',
);

export const QUICK_ACTION_TIER2 = [
  {
    id: 'resume',
    label: RESUME_DISPLAY_NAME,
    route: '/documents/resume/templates',
    creditKey: 'resume',
    icon: 'ti-file-description',
    description: 'Build a recruiter-ready resume and matching cover letter tailored to your industry.',
    cardBg: 'bg-[#FEF3C7] dark:bg-amber-950/55',
    cardBorder: 'border-[#D97706]/45 dark:border-amber-500/40',
    iconWrap: 'bg-white/90 dark:bg-amber-900/40 ring-amber-200/80 dark:ring-amber-700',
    iconColor: 'text-[#D97706] dark:text-amber-300',
    titleColor: 'text-amber-950 dark:text-amber-50',
    descColor: 'text-amber-900/75 dark:text-amber-100/80',
    creditBadge: 'bg-[#D97706] text-white dark:bg-amber-500',
  },
  {
    id: 'invoice',
    label: 'Invoice',
    route: '/documents/invoice/templates',
    creditKey: 'invoice',
    icon: 'ti-receipt',
    description: 'Create branded invoices with your logo and itemized billing.',
    cardBg: 'bg-[#FCE7F3] dark:bg-pink-950/55',
    cardBorder: 'border-[#BE185D]/45 dark:border-pink-500/40',
    iconWrap: 'bg-white/90 dark:bg-pink-900/40 ring-pink-200/80 dark:ring-pink-700',
    iconColor: 'text-[#BE185D] dark:text-pink-300',
    titleColor: 'text-pink-950 dark:text-pink-50',
    descColor: 'text-pink-900/75 dark:text-pink-100/80',
    creditBadge: 'bg-[#BE185D] text-white dark:bg-pink-600',
  },
  {
    id: 'pdf_compression',
    label: 'Compress PDF',
    route: '/services/pdf-compression',
    creditKey: 'pdf_compression',
    icon: 'ti-file-zip',
    description: 'Reduce PDF size for visa uploads (Target Size), email attachments, and submissions.',
    cardBg: 'bg-[#CCFBF1] dark:bg-teal-950/55',
    cardBorder: 'border-[#0F766E]/45 dark:border-teal-500/40',
    iconWrap: 'bg-white/90 dark:bg-teal-900/40 ring-teal-200/80 dark:ring-teal-700',
    iconColor: 'text-[#0F766E] dark:text-teal-300',
    titleColor: 'text-teal-950 dark:text-teal-50',
    descColor: 'text-teal-900/75 dark:text-teal-100/80',
    creditBadge: 'bg-[#0F766E] text-white dark:bg-teal-600',
  },
];

/** @deprecated use QUICK_ACTION_TIER1 + QUICK_ACTION_TIER2 */
export const QUICK_ACTION_ITEMS = [...QUICK_ACTION_TIER1, ...QUICK_ACTION_TIER2];

/** Document Library capsules (dashboard reference zone — unified styling). */
export const DOCUMENT_LIBRARY_ITEMS = [
  { id: 'business_proposal', name: 'Business Proposal', slug: 'business-proposal', useBuilder: true, icon: 'ti-briefcase' },
  { id: 'purchase_order', name: 'Purchase Order', slug: 'purchase-order', icon: 'ti-shopping-cart' },
  { id: 'resume', name: RESUME_DISPLAY_NAME, slug: 'resume', icon: 'ti-file-description' },
  { id: 'invoice', name: 'Invoice', slug: 'invoice', icon: 'ti-receipt' },
  { id: 'quotation', name: 'Quotation', slug: 'quotation', icon: 'ti-calculator' },
  { id: 'proforma_invoice', name: 'Proforma Invoice', slug: 'proforma-invoice', icon: 'ti-file-invoice' },
  { id: 'waybill', name: 'Waybill', slug: 'waybill', icon: 'ti-truck' },
  { id: 'delivery_note', name: 'Delivery Note', slug: 'delivery-note', icon: 'ti-package' },
];

/** @deprecated use DOCUMENT_LIBRARY_ITEMS */
export const DOCUMENT_PRICING_REFERENCE = DOCUMENT_LIBRARY_ITEMS.map((item) => ({
  ...item,
  creditKey: item.id,
}));

/** @deprecated use DOCUMENT_PRICING_REFERENCE */
export const DOCUMENT_GENERATION_TYPES = DOCUMENT_PRICING_REFERENCE.map((item) => ({
  id: item.id,
  slug: item.slug,
  name: item.name,
  iconKey: 'file',
}));

/**
 * Resolve credit cost from API maps (documents/types + service-credits).
 * @param {Record<string, number>} creditMap
 * @param {string} key
 * @param {number} [fallback=3]
 */
export function resolveCreditCost(creditMap, key, fallback = 3) {
  if (!creditMap || !key) return fallback;
  if (creditMap[key] != null) return creditMap[key];
  if (key === 'business_plan_funding' && creditMap.business_proposal != null) {
    return creditMap.business_proposal;
  }
  if (key === 'business_grant_proposal' && creditMap.business_proposal != null) {
    return creditMap.business_proposal;
  }
  if (key === 'ngo_grant_proposal' && creditMap.business_grant_proposal != null) {
    return creditMap.business_grant_proposal;
  }
  if (creditMap.document_generation != null) return creditMap.document_generation;
  return fallback;
}

export function isNavItemActive(pathname, route) {
  if (pathname === route) return true;
  if (route.startsWith('/documents/') && pathname.startsWith(route.replace('/templates', ''))) {
    return pathname.startsWith(route.replace('/templates', ''));
  }
  if (route.startsWith('/services/') && pathname.startsWith(route)) return true;
  if (route === '/templates' && pathname.startsWith('/templates')) return true;
  if (route === '/my-library' && pathname.startsWith('/my-library')) return true;
  return false;
}
